import { useMutation, UseMutationOptions, useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { FirebaseError } from 'firebase/app';

import { axiosInstance } from '@/axios';
import { fetchCollectionDocuments } from '@/queries/utils';
import { responseHandler } from '@/utils';

import {
  CreateGastronomyPointRequest,
  DeleteGastronomyPointRequest,
  GastronomyPointData,
  UpdateGastronomyPointRequest,
} from './types';

/**
 * Punkty gastronomiczne outletu: odczyt z Firestore, zapis przez backend v3 (admin SDK),
 * tak jak reszta CRUD-u room service.
 *
 * Nazwa punktu NIE jest tłumaczona, więc w odróżnieniu od kategorii czy wariantów nie ma tu
 * ani `langDict`, ani czekania na `translationStatus`: zapis jest widoczny od razu.
 */
export const GASTRONOMY_POINTS_QUERY_KEY = 'room-service-gastronomy-points';

export const useGetGastronomyPoints = (
  organizationId: string | null | undefined,
  roomServiceId: string | null | undefined,
): UseQueryResult<GastronomyPointData[], FirebaseError> => useQuery<GastronomyPointData[], FirebaseError>({
  queryKey: [GASTRONOMY_POINTS_QUERY_KEY, organizationId, roomServiceId],
  queryFn: async () => {
    if (!organizationId || !roomServiceId) return [];
    const points = await fetchCollectionDocuments<GastronomyPointData>(
      `hotels/${organizationId}/roomService/${roomServiceId}/gastronomyPoints`,
    );
    // Sortujemy po `order` w panelu, a nie zapytaniem: kolekcja liczy kilka pozycji,
    // a zapytanie z `orderBy` wymagałoby indeksu i gubiłoby punkty bez tego pola.
    return [...points].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  },
  enabled: !!organizationId && !!roomServiceId,
  retry: 0,
});

export const useCreateGastronomyPoint = (
  options?: UseMutationOptions<any, AxiosError, CreateGastronomyPointRequest>,
) => useMutation({
  mutationFn: (request) => axiosInstance
    .post('/v3-createRoomServiceGastronomyPoint', request)
    .then(responseHandler),
  ...options,
});

export const useUpdateGastronomyPoint = (
  options?: UseMutationOptions<any, AxiosError, UpdateGastronomyPointRequest>,
) => useMutation({
  mutationFn: (request) => axiosInstance
    .put('/v3-updateRoomServiceGastronomyPoint', request)
    .then(responseHandler),
  ...options,
});

// single delete deleguje do scalonego endpointu 1..N (pointIds: [id]).
export const useDeleteGastronomyPoint = (
  options?: UseMutationOptions<any, AxiosError, DeleteGastronomyPointRequest>,
) => useMutation({
  mutationFn: ({ hotelId, roomServiceId, pointIds }) => axiosInstance
    .delete('/v3-deleteRoomServiceGastronomyPoint', { data: { hotelId, roomServiceId, pointIds } })
    .then(responseHandler),
  ...options,
});
